import React from "react"

import { CodeBlock } from "@javascript/components/ui/code-block"
import {
  TypographyH2,
  TypographyH3,
  TypographyInlineCode,
  TypographyList,
  TypographyP
} from "@javascript/components/ui/typography"

interface ThemeManifestPageProps {
  themeJsonExample: string
}

export function ThemeManifestPage({ themeJsonExample }: ThemeManifestPageProps) {
  return (
    <div className="space-y-8">
      <div>
        <TypographyH2>theme.json Manifest Reference</TypographyH2>
        <TypographyP>
          The <TypographyInlineCode>theme.json</TypographyInlineCode> manifest declares which platform blocks your theme uses and how to identify them.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Real example</TypographyH3>
        <CodeBlock code={themeJsonExample} language="json" className="my-4" />
      </div>

      <div>
        <TypographyH3>Required fields</TypographyH3>
        <TypographyP>Every manifest must include all four fields:</TypographyP>
        <TypographyList>
          <li>
            <TypographyInlineCode>name</TypographyInlineCode> — the human-readable name of your theme (e.g., <TypographyInlineCode>"Elegant"</TypographyInlineCode>)
          </li>
          <li>
            <TypographyInlineCode>version</TypographyInlineCode> — semantic version of your theme package (e.g., <TypographyInlineCode>"1.0.0"</TypographyInlineCode>)
          </li>
          <li>
            <TypographyInlineCode>contract_version</TypographyInlineCode> — must be <TypographyInlineCode>3</TypographyInlineCode>. Contract 3 is the only accepted contract; its trusted runtime remains optional
          </li>
          <li>
            <TypographyInlineCode>blocks</TypographyInlineCode> — array of block instances your theme declares
          </li>
        </TypographyList>
      </div>

      <div>
        <TypographyH3>Optional trusted runtime in contract v3</TypographyH3>
        <TypographyP>
          A curated contract v3 theme may declare one optional browser entrypoint and its platform-approved dependencies:
        </TypographyP>
        <CodeBlock
          code={`"runtime": {
  "entrypoint": "assets/theme.js",
  "dependencies": ["jquery-3"]
}`}
          language="json"
          className="my-4"
        />
        <TypographyList>
          <li><TypographyInlineCode>entrypoint</TypographyInlineCode> must name an existing <TypographyInlineCode>.js</TypographyInlineCode> file inside the package's <TypographyInlineCode>assets/</TypographyInlineCode> directory.</li>
          <li>The entrypoint runs as a classic deferred script. Goldypaper does not compile it, so commit browser-ready JavaScript without package imports.</li>
          <li><TypographyInlineCode>dependencies</TypographyInlineCode> defaults to an empty array and accepts only platform allowlist IDs. <TypographyInlineCode>jquery-3</TypographyInlineCode> is currently available and exposes <TypographyInlineCode>window.jQuery</TypographyInlineCode> and <TypographyInlineCode>window.$</TypographyInlineCode>.</li>
          <li>The platform loads dependencies in manifest order, then loads the entrypoint. Do not add script tags to <TypographyInlineCode>template.liquid</TypographyInlineCode>.</li>
        </TypographyList>
      </div>

      <div>
        <TypographyH3>Block entries: id and type</TypographyH3>
        <TypographyP>
          Each entry in the <TypographyInlineCode>blocks</TypographyInlineCode> array contains two required fields and one optional field:
        </TypographyP>
        <TypographyList>
          <li>
            <TypographyInlineCode>id</TypographyInlineCode> — the <strong>instance identifier</strong> unique to this theme. Use it in <TypographyInlineCode>template.liquid</TypographyInlineCode> via <TypographyInlineCode>{`{% block "id" %}`}</TypographyInlineCode>. Example: <TypographyInlineCode>"hero"</TypographyInlineCode>, <TypographyInlineCode>"schedule"</TypographyInlineCode>, or <TypographyInlineCode>"gallery"</TypographyInlineCode>.
          </li>
          <li>
            <TypographyInlineCode>type</TypographyInlineCode> — the <strong>platform block type</strong> it uses. Current types are <TypographyInlineCode>"hero"</TypographyInlineCode>, <TypographyInlineCode>"couple"</TypographyInlineCode>, <TypographyInlineCode>"countdown"</TypographyInlineCode>, <TypographyInlineCode>"event_schedule"</TypographyInlineCode>, <TypographyInlineCode>"story"</TypographyInlineCode>, <TypographyInlineCode>"wedding_party"</TypographyInlineCode>, <TypographyInlineCode>"gallery"</TypographyInlineCode>, <TypographyInlineCode>"guest_information"</TypographyInlineCode>, and <TypographyInlineCode>"rsvp"</TypographyInlineCode>. The type selects presentation behavior, not storage.
          </li>
          <li>
            <TypographyInlineCode>config</TypographyInlineCode> — optional, allowlisted presentation options for that block type. Config can change presentation but cannot define data fields or behavior.
          </li>
        </TypographyList>
        <TypographyP className="text-sm text-muted-foreground">
          <strong>Key principle:</strong> Themes only <em>use</em> blocks; the platform <em>defines</em> them. Your manifest declares which predefined blocks your theme layout includes.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Instance id vs. type: when they match</TypographyH3>
        <TypographyP>
          In many themes, the instance id matches the type name (e.g., <TypographyInlineCode>{`{ "id": "hero", "type": "hero" }`}</TypographyInlineCode>). This is a convention, not a requirement. They can differ: you could name an instance <TypographyInlineCode>"cover"</TypographyInlineCode> for a block of type <TypographyInlineCode>"hero"</TypographyInlineCode> if you want a more semantic instance name in your theme. The type is what matters to the platform; the id is what matters to your template.
        </TypographyP>
      </div>

      <div>
        <TypographyH3>Instance IDs are presentation-local</TypographyH3>
        <TypographyP>
          An instance ID identifies a slot inside one theme; it is never a content-storage key. All block renderers read the invitation's canonical wedding facts, so renaming a slot or switching themes cannot orphan the couple's content.
        </TypographyP>
      </div>
    </div>
  )
}
